import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { FaRupeeSign, FaDownload, FaRedo } from 'react-icons/fa';
import { calculateEPF, formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';
import { isPrerender } from '../utils/prerender';

ChartJS.register(ArcElement, Tooltip, Legend);

const EPFCalculator = () => {
  const shouldRenderChart = !isPrerender();
  const [basicSalary, setBasicSalary] = useState(50000);
  const [currentAge, setCurrentAge] = useState(25);
  const [retirementAge, setRetirementAge] = useState(60);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [employeeContribution, setEmployeeContribution] = useState(12);
  const [employerContribution, setEmployerContribution] = useState(3.67);
  const [salaryIncrement, setSalaryIncrement] = useState(5);
  const [interestRate, setInterestRate] = useState(8.25);
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [basicSalary, currentAge, retirementAge, currentBalance, employeeContribution, 
      employerContribution, salaryIncrement, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    const bs = Number(basicSalary) || 0;
    const ec = Number(employeeContribution) || 0;
    const erc = Number(employerContribution) || 0;
    const ca = Number(currentAge) || 0;
    const ra = Number(retirementAge) || 0;
    const cb = Number(currentBalance) || 0;
    const si = Number(salaryIncrement) || 0;
    const ir = Number(interestRate) || 0;
    const epfResult = calculateEPF(
      bs, ec, erc,
      ca, ra, cb, si, ir
    );
    setResult(epfResult);
  };

  const handleDownloadPDF = () => {
    const totalContribution = result.yearlyData.reduce(
      (sum, year) => sum + year.employeeContribution + year.employerContribution, 0
    );
    generateGenericPDF('EPF Calculator Report', {
      'Basic Salary (Monthly)': basicSalary,
      'Current Age': currentAge,
      'Retirement Age': retirementAge,
      'Years to Retirement': retirementAge - currentAge,
      'Employee Contribution': `${employeeContribution}%`,
      'Employer Contribution': `${employerContribution}%`,
      'Interest Rate': `${interestRate}%`,
      'Total Contribution': Math.round(totalContribution),
      'Maturity Amount': result.maturityAmount,
    });
  };

  const handleReset = () => {
    setBasicSalary(50000);
    setCurrentAge(25);
    setRetirementAge(60);
    setCurrentBalance(0);
    setEmployeeContribution(12);
    setEmployerContribution(3.67);
    setSalaryIncrement(5);
    setInterestRate(8.25);
  };

  const totalContribution = result ? result.yearlyData.reduce(
    (sum, year) => sum + year.employeeContribution + year.employerContribution, 0
  ) : 0;
  const totalInterest = result ? result.maturityAmount - totalContribution - currentBalance : 0;

  const chartData = result ? {
    labels: ['Total Contribution', 'Total Interest'],
    datasets: [{
      data: [Math.round(totalContribution), Math.round(totalInterest)],
      backgroundColor: ['#ec4899', '#10b981'],
      borderColor: ['#db2777', '#059669'],
      borderWidth: 2,
    }],
  } : null;

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="EPF Calculator"
        description="Free EPF Calculator India - Calculate Employee Provident Fund maturity. Current EPF rate 8.25%. Employer & employee contribution calculator."
        keywords="EPF calculator, EPF calculator India, employee provident fund calculator, EPF maturity calculator, EPF contribution calculator, PF calculator"
        canonicalPath="/epf"
      />
      <div className="calculator-header">
        <h1>EPF Calculator</h1>
        <p>Estimate your Employee Provident Fund corpus at retirement</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Basic Salary (Monthly) (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={basicSalary === '' ? '' : basicSalary}
                onChange={(e) => setBasicSalary(e.target.value === '' ? '' : Number(e.target.value))}
                min="15000"
                max="500000"
              />
            </div>
          </div>

          <div className="grid-2-cols">
            <div className="input-group">
              <label>Current Age</label>
              <input
                type="number"
                value={currentAge === '' ? '' : currentAge}
                onChange={(e) => setCurrentAge(e.target.value === '' ? '' : Number(e.target.value))}
                min="18"
                max="58"
              />
            </div>

            <div className="input-group">
              <label>Retirement Age</label>
              <input
                type="number"
                value={retirementAge === '' ? '' : retirementAge}
                onChange={(e) => setRetirementAge(e.target.value === '' ? '' : Number(e.target.value))}
                min="58"
                max="70"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Current EPF Balance (₹)</label>
            <input
              type="number"
              value={currentBalance === '' ? '' : currentBalance}
              onChange={(e) => setCurrentBalance(e.target.value === '' ? '' : Number(e.target.value))}
              min="0"
            />
          </div>

          <div className="grid-2-cols">
            <div className="input-group">
              <label>Employee Contribution (%)</label>
              <input
                type="number"
                value={employeeContribution === '' ? '' : employeeContribution}
                onChange={(e) => setEmployeeContribution(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                max="100"
              />
            </div>

            <div className="input-group">
              <label>Employer Contribution (%)</label>
              <input
                type="number"
                value={employerContribution === '' ? '' : employerContribution}
                onChange={(e) => setEmployerContribution(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                max="100"
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                3.67% goes to EPF, 8.33% to EPS
              </small>
            </div>
          </div>

          <div className="grid-2-cols">
            <div className="input-group">
              <label>Expected Salary Increment (%)</label>
              <input
                type="number"
                value={salaryIncrement === '' ? '' : salaryIncrement}
                onChange={(e) => setSalaryIncrement(e.target.value === '' ? '' : Number(e.target.value))}
                min="0"
                max="20"
              />
            </div>

            <div className="input-group">
              <label>Interest Rate (%)</label>
              <input
                type="number"
                value={interestRate === '' ? '' : interestRate}
                onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
                min="1"
                max="15"
                step="0.01"
              />
            </div>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary">
                <h3>EPF Corpus at Retirement</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Years to Retirement</span>
                  <span className="value">{retirementAge - currentAge} years</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Contribution</span>
                  <span className="value">{formatCurrency(totalContribution)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#10b981' }}>
                    +{formatCurrency(totalInterest)}
                  </span>
                </div>
              </div>

              {shouldRenderChart && (
                <div className="chart-container">
                  <Pie data={chartData} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>About EPF (Employee Provident Fund)</h4>
        <ul>
          <li><strong>Employee Contribution:</strong> 12% of Basic + DA goes to EPF</li>
          <li><strong>Employer Contribution:</strong> 3.67% to EPF, 8.33% to EPS (Pension Scheme)</li>
          <li><strong>Current Interest Rate:</strong> 8.25% for FY 2024-25</li>
          <li><strong>Tax Benefit:</strong> Contribution up to ₹1.5 lakh qualifies for 80C deduction</li>
          <li><strong>Taxability:</strong> Interest on contribution above ₹2.5 lakh is taxable</li>
        </ul>
      </div>
    </div>
  );
};

export default EPFCalculator;
